import { http } from './http'
import { PaginationParams } from './map'

export interface IVendorLocation {
  id: string
  name: string
  users: number
  products: number
  tags: number
}

export interface IVendor {
  id: string
  name: string
  products: number
  users: number
  locations: number
}

export interface IVendorDetails {
  id: string
  name: string
  locations: IVendorLocation[]
}

export interface VendorPayload {
  name: string
  locations: string[]
}

export type VendorUpdatePayload = {
  name: string
  locations: {
    id: string
    name: string
  }[]
}

export const getVendors = (
  params?: PaginationParams & { festivalEditionId: string },
) =>
  http.get<IVendor[]>('/operations/vendor', {
    params,
  })

export interface IVendorTag {
  id: string
  name: string
  createdAt: number
}
export const getVendorTags = (id: string) =>
  http.get<IVendorTag[]>(`/operations/vendor/${id}/tag`)

export const createVendorTag = (id: string, data: IVendorTag) =>
  http.post<IVendorTag>(`/operations/vendor/${id}/tag`, data)

export const updateVendorTag = (
  vendorId: string,
  tagId: string,
  data: { name: string },
) => http.put<IVendorTag>(`/operations/vendor/${vendorId}/tag/${tagId}`, data)

export const deleteVendorTag = (vendorId: string, tagId: string) =>
  http.delete(`/operations/vendor/${vendorId}/tag/${tagId}`)

export interface IVendorProductPayload {
  id: string
  name: string
  description?: string
  imageLink: string
  tags: string[]
  price: number
}

export interface IVendorProduct {
  id: string
  name: string
  description?: string
  imageLink: string
  tags: string[]
  price: number
}

export const getVendorProducts = (id: string) =>
  http.get<IVendorProduct[]>(`/operations/vendor/${id}/product`)

export const createVendorProduct = (
  id: string,
  data: Omit<IVendorProductPayload, 'id'>,
) => http.post<IVendorProduct>(`/operations/vendor/${id}/product`, data)

export const updateVendorProduct = (
  vendorId: string,
  productId: string,
  data: Omit<IVendorProductPayload, 'id'>,
) =>
  http.put<IVendorProduct>(
    `/operations/vendor/${vendorId}/product/${productId}`,
    data,
  )

export const deleteVendorProduct = (vendorId: string, productId: string) =>
  http.delete(`/operations/vendor/${vendorId}/product/${productId}`)

export type VendorUserPermission =
  | 'LOCATIONS'
  | 'USERS'
  | 'TAGS'
  | 'PRODUCTS'
  | 'SALES'

export interface IVendorUser {
  id: string
  name: string
  phone: string
  permissions: VendorUserPermission[]
}

export interface IVendorLocationDetails {
  users: IVendorUser[]
  products: IVendorProduct[]
}

export const getVendorUsers = (id: string) =>
  http.get<IVendorUser[]>(`/operations/vendor/${id}/user`)

export const createVendorUser = (id: string, data: IVendorUser) =>
  http.post<IVendorUser>(`/operations/vendor/${id}/user`, data)

export const updateVendorUser = (
  vendorId: string,
  userId: string,
  data: Omit<IVendorUser, 'id'>,
) =>
  http.put<IVendorUser>(`/operations/vendor/${vendorId}/user/${userId}`, data)

export const deleteVendorUser = (vendorId: string, userId: string) =>
  http.delete(`/operations/vendor/${vendorId}/user/${userId}`)

export const getVendorLocations = (id: string) =>
  http.get<IVendorLocation[]>(`/operations/vendor/${id}/location`)

export const getVendorLocation = (vendorId: string, locationId: string) =>
  http.get<IVendorLocationDetails>(
    `/operations/vendor/${vendorId}/location/${locationId}`,
  )

export const assignVendorLocationUser = (
  vendorId: string,
  locationId: string,
  data: { ids: string[] },
) =>
  http.put(`/operations/vendor/${vendorId}/location/${locationId}/users`, data)

export const assignVendorLocationProduct = (
  vendorId: string,
  locationId: string,
  data: { ids: string[] },
) =>
  http.put(
    `/operations/vendor/${vendorId}/location/${locationId}/products`,
    data,
  )

export const removeVendorLocationUser = (
  vendorId: string,
  locationId: string,
  userId: string,
) =>
  http.delete(
    `/operations/vendor/${vendorId}/location/${locationId}/users/${userId}`,
  )

export const removeVendorLocationProduct = (
  vendorId: string,
  locationId: string,
  productId: string,
) =>
  http.delete(
    `/operations/vendor/${vendorId}/location/${locationId}/products/${productId}`,
  )

export const createVendorLocation = (
  id: string,
  data: Pick<IVendorLocation, 'id' | 'name'>[],
) => http.post<IVendorLocation>(`/operations/vendor/${id}/location`, data)

export const updateVendorLocation = (
  vendorId: string,
  locationId: string,
  data: { name: string },
) =>
  http.put<IVendorLocation>(
    `/operations/vendor/${vendorId}/location/${locationId}`,
    data,
  )

export const deleteVendorLocation = (vendorId: string, locationId: string) =>
  http.delete(`/operations/vendor/${vendorId}/location/${locationId}`)

export const createVendor = (data: VendorPayload) =>
  http.post<IVendor>('/operations/vendor', data)

export const updateVendor = (id: string, payload: VendorPayload) =>
  http.put<IVendor>(`/operations/vendor/${id}`, payload)

export const deleteVendor = (ids: string[]) =>
  http.delete('/operations/vendor', {
    data: { ids },
  })
